import { ProductItem, RealEstateProject } from './types';

// Helper to determine if a category is a product-selling / retail / ecommerce business
export function isProductBusiness(category?: string | null): boolean {
  if (!category) return false;
  const c = category.toLowerCase();
  return /soap|oil|masala|spice|saree|dress|cloth|garment|apparel|shoe|electronics|mobile|computer|gadget|bluetooth|bike|car|automobile|software|medicine|pharmacy|handicraft|kitchen|utensil|furniture|hardware|book|stationery|jewellery|jewelry|sweet|bakery|supermarket|store|shop|retail|wholesale|dealer|distributor|equipment|toy|cosmetic|beauty product/i.test(c);
}

// Helper to determine if a category is real estate / builder / property business
export function isRealEstateBusiness(category?: string | null): boolean {
  if (!category) return false;
  const c = category.toLowerCase();
  return /real estate|builder|developer|apartment|promoter|housing|property|villa|plot|construction|architect|realtor|estate agent/i.test(c);
}

// Generates high-fidelity default product catalog for product businesses
export function generateDefaultProducts(businessName: string, category: string, city: string): ProductItem[] {
  const c = category.toLowerCase();

  // 1. Handmade Soaps / Cosmetics / Personal Care
  if (/soap|shampoo|cosmetic|beauty|skincare|hair oil/i.test(c)) {
    return [
      {
        id: 'prod-1',
        title: 'Organic Charcoal & Tea Tree Handmade Soap Bar',
        category: 'Skin Care',
        price: 249,
        original_price: 349,
        currency: '₹',
        images: ['https://images.unsplash.com/photo-1607006314045-817e0b571120?auto=format&fit=crop&w=600&q=80'],
        description: 'Cold-processed, 100% natural handmade soap crafted with activated bamboo charcoal, pure tea tree essential oil, and unrefined shea butter for deep detox and clear skin.',
        features: ['100% Vegan & Cruelty Free', 'Chemical & Paraben Free', 'Suitable for all skin types', 'Handcrafted in Small Batches'],
        in_stock: true,
        sku: 'HSOAP-CHAR-01',
        badge: 'Best Seller'
      },
      {
        id: 'prod-2',
        title: 'Pure Goat Milk & Kashmiri Saffron Bath Bar',
        category: 'Skin Care',
        price: 299,
        original_price: 399,
        currency: '₹',
        images: ['https://images.unsplash.com/photo-1608248597359-25f02fef9723?auto=format&fit=crop&w=600&q=80'],
        description: 'Luxurious hydrating soap enriched with fresh farm goat milk, Kashmiri mongra saffron strands, and sweet almond oil for radiant, baby-soft skin.',
        features: ['Deep Moisturizing', 'Rich in Vitamin A & E', 'Soothes Dry Skin', 'Zero Artificial Fragrance'],
        in_stock: true,
        sku: 'HSOAP-SAFF-02',
        badge: 'Handmade'
      },
      {
        id: 'prod-3',
        title: 'Ayurvedic Bhringraj & Rosemary Scalp Therapy Hair Oil',
        category: 'Hair Care',
        price: 499,
        original_price: 699,
        currency: '₹',
        images: ['https://images.unsplash.com/photo-1608248543803-ba4f8c70ae0b?auto=format&fit=crop&w=600&q=80'],
        description: 'Traditional slow-infused herbal hair oil with wild Bhringraj, Rosemary, Amla, and Hibiscus in cold-pressed virgin sesame oil for hair fall control and follicle nourishment.',
        features: ['Controls Hair Fall', 'Promotes Hair Growth', 'Non-Sticky Formula', '100% Natural Herbs'],
        in_stock: true,
        sku: 'HOIL-BHRING-03',
        badge: 'Trending'
      },
      {
        id: 'prod-4',
        title: 'Cold-Pressed Virgin Coconut & Aloe Vera Gentle Body Wash',
        category: 'Body Care',
        price: 389,
        original_price: 499,
        currency: '₹',
        images: ['https://images.unsplash.com/photo-1556228720-195a672e8a03?auto=format&fit=crop&w=600&q=80'],
        description: 'Sulfate-free nourishing body wash formulated with pure organic coconut extract, soothing aloe vera gel, and vitamin E.',
        features: ['Sulfate & Paraben Free', 'pH Balanced 5.5', 'Long-lasting Freshness', 'Gentle Daily Cleanser'],
        in_stock: true,
        sku: 'BWASH-COCO-04',
        badge: 'New Arrival'
      }
    ];
  }

  // 2. Indian Masalas / Spices / Cooking Oils / Food Products
  if (/masala|spice|cooking oil|oil|bakery|sweet|food/i.test(c)) {
    return [
      {
        id: 'prod-m1',
        title: 'Authentic Homemade Sambar Powder (Zero Preservatives) - 500g',
        category: 'Traditional Masalas',
        price: 260,
        original_price: 320,
        currency: '₹',
        images: ['https://images.unsplash.com/photo-1596040033229-a9821ebd058d?auto=format&fit=crop&w=600&q=80'],
        description: 'Hand-roasted traditional blend of Guntur red chilies, coriander seeds, cumin, fenugreek, and roasted dals crafted according to generational South Indian recipes.',
        features: ['Stone Ground Artisan Blend', 'No Added Preservatives or Colors', 'Rich Aroma & Authentic Taste', 'Vacuum Sealed Freshness'],
        in_stock: true,
        sku: 'MASALA-SAMBAR-500',
        badge: 'Best Seller'
      },
      {
        id: 'prod-m2',
        title: 'Royal Garam Masala (Whole Spice Stone-Ground) - 250g',
        category: 'Specialty Spices',
        price: 199,
        original_price: 249,
        currency: '₹',
        images: ['https://images.unsplash.com/photo-1509358271058-acd22cc93898?auto=format&fit=crop&w=600&q=80'],
        description: 'Aromatic royal blend with black cardamom, green cardamom, Ceylon cinnamon, mace, nutmeg, and cloves for biryanis and curries.',
        features: ['100% Whole Spices', 'Zero Fillers', 'Intense Flavour Profile', 'Small Batch Roasted'],
        in_stock: true,
        sku: 'MASALA-GARAM-250',
        badge: 'Premium'
      },
      {
        id: 'prod-m3',
        title: 'Wood Pressed (Chekku) Pure Groundnut Cooking Oil - 1 Litre',
        category: 'Cold Pressed Oils',
        price: 295,
        original_price: 350,
        currency: '₹',
        images: ['https://images.unsplash.com/photo-1474979266404-7eaacbcd87c5?auto=format&fit=crop&w=600&q=80'],
        description: 'Extracted using traditional wooden churner (Mara Chekku) from premium sun-dried Saurashtra groundnuts. Retains natural nutrients, antioxidants, and aroma.',
        features: ['Cold Pressed / Zero Heat', 'Unrefined & Unfiltered', 'Heart-Healthy Monounsaturated Fats', 'High Smoke Point'],
        in_stock: true,
        sku: 'OIL-WOOD-GND-1L',
        badge: '100% Pure'
      },
      {
        id: 'prod-m4',
        title: 'Pure Single-Origin Alleppey Turmeric Powder (Curcumin 5%+) - 500g',
        category: 'Daily Spices',
        price: 180,
        original_price: 220,
        currency: '₹',
        images: ['https://images.unsplash.com/photo-1615485290382-441e4d049cb5?auto=format&fit=crop&w=600&q=80'],
        description: 'Single-origin Alleppey finger turmeric ground fresh with high natural curcumin content for health, cooking, and golden milk.',
        features: ['High Curcumin Content (>5%)', 'Pesticide Free Farm Sourced', 'Zero Added Starch or Color', 'Immunity Booster'],
        in_stock: true,
        sku: 'SPICE-TURM-500',
        badge: 'Organic'
      }
    ];
  }

  // 3. Sarees / Dresses / Garments / Textiles
  if (/saree|dress|clothing|garment|apparel|textile|boutique/i.test(c)) {
    return [
      {
        id: 'prod-s1',
        title: 'Pure Kanchipuram Silk Bridal Saree with Gold Zari Border',
        category: 'Bridal Sarees',
        price: 14500,
        original_price: 18900,
        currency: '₹',
        images: ['https://images.unsplash.com/photo-1610030469983-98e550d6193c?auto=format&fit=crop&w=600&q=80'],
        description: 'Authentic handwoven pure mulberry silk saree featuring intricate temple korvai borders, rich pallu, and certified Silk Mark guarantee.',
        features: ['Silk Mark Certified', 'Pure Zari Weaving', 'Unstitched Blouse Piece Included', 'Bridal Heritage Collection'],
        in_stock: true,
        sku: 'SR-KANCHI-GOLD-01',
        badge: 'Silk Mark Certified'
      },
      {
        id: 'prod-s2',
        title: 'Handwoven Banarasi Georgette Meenakari Zari Saree',
        category: 'Party Wear',
        price: 6800,
        original_price: 8500,
        currency: '₹',
        images: ['https://images.unsplash.com/photo-1617627143750-d86bc21e42bb?auto=format&fit=crop&w=600&q=80'],
        description: 'Lightweight soft Banarasi georgette saree embellished with delicate dual-tone gold and silver Meenakari floral motifs.',
        features: ['Lightweight & Breathable', 'Dual Tone Zari Work', 'Elegant Drape', 'Festive Ready'],
        in_stock: true,
        sku: 'SR-BANARAS-GEO-02',
        badge: 'Trending'
      },
      {
        id: 'prod-s3',
        title: 'Handblock Printed Pure Chanderi Silk Kurti & Dupatta Set',
        category: 'Ethnic Sets',
        price: 2450,
        original_price: 3200,
        currency: '₹',
        images: ['https://images.unsplash.com/photo-1583391733956-3750e0ff4e8b?auto=format&fit=crop&w=600&q=80'],
        description: 'Traditional Bagru handblock printed Chanderi kurti paired with matching organza dupatta and cotton pants.',
        features: ['Natural Vegetable Dyes', 'Comfort Fit with Lining', 'Artisanal Handcraft', 'Sizes: S, M, L, XL, XXL'],
        in_stock: true,
        sku: 'SET-CHAND-KURTI-03',
        badge: 'Best Seller'
      }
    ];
  }

  // 4. Sports Goods & Equipment / Gyms
  if (/sport|fitness|gym|equipment/i.test(c)) {
    return [
      {
        id: 'prod-sp1',
        title: 'Professional Grade English Willow Cricket Bat (Grade 1)',
        category: 'Cricket Equipment',
        price: 7999,
        original_price: 10999,
        currency: '₹',
        images: ['https://images.unsplash.com/photo-1540747913346-19e32dc3e97e?auto=format&fit=crop&w=600&q=80'],
        description: 'Hand-crafted Grade 1 English Willow cricket bat with massive edges, pronounced sweet spot, and multi-piece cane handle for unmatched power and shock absorption.',
        features: ['Grade 1 English Willow', 'Pre-Knocked & Ready to Play', 'Weight: 1160 - 1200g', 'Includes Padded Bat Cover'],
        in_stock: true,
        sku: 'CRIC-BAT-PRO1',
        badge: 'Top Rated'
      },
      {
        id: 'prod-sp2',
        title: 'Full Graphite High-Tension Badminton Racket Dual Pack with Kitbag',
        category: 'Badminton',
        price: 3499,
        original_price: 4899,
        currency: '₹',
        images: ['https://images.unsplash.com/photo-1626224583764-f87db24ac4ea?auto=format&fit=crop&w=600&q=80'],
        description: 'High modulus carbon graphite isometric rackets supporting up to 30 lbs string tension, paired with full thermal racket bag and nylon shuttlecocks.',
        features: ['Isometric Head Frame', '30 lbs Max String Tension', 'Weight: 4U (82-84g)', 'Includes 2 Rackets + 6 Shuttles'],
        in_stock: true,
        sku: 'BADM-GRAPH-DUO',
        badge: 'Combo Deal'
      },
      {
        id: 'prod-sp3',
        title: 'Commercial Hex Rubber Dumbbell Pair Set (5kg to 25kg)',
        category: 'Gym & Fitness',
        price: 4200,
        original_price: 5500,
        currency: '₹',
        images: ['https://images.unsplash.com/photo-1584735935682-2f2b69dff9d2?auto=format&fit=crop&w=600&q=80'],
        description: 'Heavy-duty cast iron hexagonal dumbbells encased in premium virgin rubber with ergonomic chrome knurled handles.',
        features: ['Anti-Roll Hex Design', 'Odor-Free Premium Rubber', 'Solid Steel Chrome Handle', 'Drop-Resistant Durability'],
        in_stock: true,
        sku: 'GYM-HEX-DB-SET',
        badge: 'Wholesale Price'
      }
    ];
  }

  // 5. Electronics / Gadgets / Bluetooth / Software / Generic Products
  return [
    {
      id: 'prod-g1',
      title: `${businessName} Premium Certified Product Edition`,
      category: category,
      price: 1299,
      original_price: 1899,
      currency: '₹',
      images: ['https://images.unsplash.com/photo-1523275335684-37898b6baf30?auto=format&fit=crop&w=600&q=80'],
      description: `Official genuine ${category} offering from ${businessName} in ${city}. Verified authentic quality with full warranty and express local delivery.`,
      features: ['100% Genuine & Brand Certified', 'Manufacturer Warranty Included', 'Same Day / Express Dispatch', 'Easy Support & Replacement'],
      in_stock: true,
      sku: `PROD-${city.toUpperCase().slice(0, 3)}-01`,
      badge: 'Best Seller'
    },
    {
      id: 'prod-g2',
      title: `${businessName} Pro Package & Accessories Kit`,
      category: category,
      price: 2499,
      original_price: 3299,
      currency: '₹',
      images: ['https://images.unsplash.com/photo-1505740420928-5e560c06d30e?auto=format&fit=crop&w=600&q=80'],
      description: `Enhanced professional grade ${category} package with complete toolkit and direct business support from ${businessName}.`,
      features: ['High Durability Standard', 'Full Specification Compliance', 'Bulk Quantity Discounts Available', 'Dedicated Customer Assistance'],
      in_stock: true,
      sku: `PROD-${city.toUpperCase().slice(0, 3)}-02`,
      badge: 'Special Offer'
    },
    {
      id: 'prod-g3',
      title: `${businessName} Value Pack Edition`,
      category: category,
      price: 899,
      original_price: 1199,
      currency: '₹',
      images: ['https://images.unsplash.com/photo-1546868871-7041f2a55e12?auto=format&fit=crop&w=600&q=80'],
      description: `Top-rated economical product bundle customized for customers in ${city} and across India.`,
      features: ['Unmatched Value for Money', 'Top Rated Quality', 'Ready Stock Available', '100% Verified Performance'],
      in_stock: true,
      sku: `PROD-${city.toUpperCase().slice(0, 3)}-03`,
      badge: 'Value Pack'
    }
  ];
}

// Generates premium verified real estate projects for builders/realtors
export function generateDefaultRealEstateProjects(builderName: string, city: string): RealEstateProject[] {
  return [
    {
      id: 'proj-1',
      project_name: `${builderName} Codename Skyward Grandeur`,
      developer_name: builderName,
      project_type: 'Apartments',
      status: 'Under Construction',
      location: `Prime Landmark Corridor, ${city}`,
      city: city,
      price_range: '₹58.5 Lakhs - ₹1.45 Cr',
      possession_date: 'Dec 2026',
      rera_id: `RERA/${city.toUpperCase().slice(0, 3)}/PRJ/2024/0981`,
      hero_image: 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?auto=format&fit=crop&w=1000&q=80',
      gallery: [
        'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?auto=format&fit=crop&w=800&q=80',
        'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=800&q=80',
        'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&w=800&q=80'
      ],
      configurations: [
        { type: '2 BHK Luxury', builtup_area: '1,085 - 1,240 sq.ft', price: '₹58.5 L - ₹68 L' },
        { type: '3 BHK Royal', builtup_area: '1,490 - 1,780 sq.ft', price: '₹84.5 L - ₹98 L' },
        { type: '4 BHK Sky Suite', builtup_area: '2,250 - 2,800 sq.ft', price: '₹1.25 Cr - ₹1.45 Cr' }
      ],
      floor_plans: [
        { title: '2 BHK Unit Plan', image: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=600&q=80', area: '1,150 sq.ft' },
        { title: '3 BHK Unit Plan', image: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&w=600&q=80', area: '1,620 sq.ft' }
      ],
      amenities: [
        'Grand Clubhouse (25,000 sq.ft)',
        'Infinity Edge Swimming Pool',
        'Fully Equipped Gymnasium',
        'Landscaped Zen Gardens',
        'Multipurpose Badminton & Squash Courts',
        '24/7 3-Tier Security with CCTV',
        'EV Vehicle Charging Stations',
        'Kids Adventure Play Park',
        '100% Power Backup',
        'High-Speed Automatic Elevators'
      ],
      landmark_distances: [
        { name: 'Metro Station', distance: '1.2 km (4 mins)' },
        { name: 'International Airport', distance: '18 km (25 mins)' },
        { name: 'Top IT Technology Park', distance: '3.5 km (8 mins)' },
        { name: 'Leading Multi-Speciality Hospital', distance: '2.0 km (5 mins)' },
        { name: 'International School & College', distance: '1.5 km (3 mins)' }
      ],
      brochure_url: '#'
    },
    {
      id: 'proj-2',
      project_name: `${builderName} Emerald Gated Villas`,
      developer_name: builderName,
      project_type: 'Luxury Villas',
      status: 'Ready to Move',
      location: `Greenview Boulevard, ${city}`,
      city: city,
      price_range: '₹1.65 Cr - ₹2.85 Cr',
      possession_date: 'Ready to Move',
      rera_id: `RERA/${city.toUpperCase().slice(0, 3)}/VIL/2023/0412`,
      hero_image: 'https://images.unsplash.com/photo-1613977257363-707ba9348227?auto=format&fit=crop&w=1000&q=80',
      gallery: [
        'https://images.unsplash.com/photo-1613977257363-707ba9348227?auto=format&fit=crop&w=800&q=80',
        'https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?auto=format&fit=crop&w=800&q=80'
      ],
      configurations: [
        { type: '3 BHK Independent Villa', builtup_area: '2,200 sq.ft (Plot 1,500 sq.ft)', price: '₹1.65 Cr' },
        { type: '4 BHK Luxury Courtyard Villa', builtup_area: '3,150 sq.ft (Plot 2,400 sq.ft)', price: '₹2.35 Cr' },
        { type: '5 BHK Presidential Villa', builtup_area: '4,400 sq.ft (Plot 3,600 sq.ft)', price: '₹2.85 Cr' }
      ],
      floor_plans: [
        { title: 'Ground Floor Master Plan', image: 'https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?auto=format&fit=crop&w=600&q=80', area: '1,800 sq.ft' },
        { title: 'First Floor Terrace Plan', image: 'https://images.unsplash.com/photo-1613977257363-707ba9348227?auto=format&fit=crop&w=600&q=80', area: '1,350 sq.ft' }
      ],
      amenities: [
        'Private Garden & Plunge Pool Option',
        'Exclusive Villa Clubhouse',
        'Covered 2-Car Parking Port',
        'Underground Cabling & Drainage',
        'Jogging Track & Gazebo Seating',
        'Solar Water Heating System'
      ],
      landmark_distances: [
        { name: 'Highway Junction', distance: '2.5 km (6 mins)' },
        { name: 'Shopping Mall & Multiplex', distance: '4.0 km (10 mins)' },
        { name: 'City Center Bus Terminus', distance: '6.0 km (15 mins)' }
      ],
      brochure_url: '#'
    },
    {
      id: 'proj-3',
      project_name: `${builderName} Future City Plots & Commercial`,
      developer_name: builderName,
      project_type: 'Plots',
      status: 'Pre-Launch',
      location: `Growth Corridor Phase 2, ${city}`,
      city: city,
      price_range: '₹28 Lakhs - ₹75 Lakhs',
      possession_date: 'Immediate Registration',
      rera_id: `RERA/${city.toUpperCase().slice(0, 3)}/PLT/2026/0115`,
      hero_image: 'https://images.unsplash.com/photo-1500382017468-9049fed747ef?auto=format&fit=crop&w=1000&q=80',
      gallery: [
        'https://images.unsplash.com/photo-1500382017468-9049fed747ef?auto=format&fit=crop&w=800&q=80'
      ],
      configurations: [
        { type: 'Residential Plot (600 sq.ft)', builtup_area: '600 sq.ft (20 x 30)', price: '₹28 Lakhs' },
        { type: 'Villa Plot (1200 sq.ft)', builtup_area: '1,200 sq.ft (30 x 40)', price: '₹48 Lakhs' },
        { type: 'Commercial Corner Plot', builtup_area: '2,400 sq.ft (40 x 60)', price: '₹75 Lakhs' }
      ],
      amenities: [
        'DTCP / CMDA Approved Layout',
        '40-Feet Wide Blacktop Roads',
        'Street Lighting & Avenue Trees',
        '24/7 Gated Entry with Security',
        'Clear Title & 100% Bank Loan Approved'
      ],
      landmark_distances: [
        { name: 'Upcoming Ring Road', distance: '800 meters (2 mins)' },
        { name: 'Industrial Hub & SEZ', distance: '5 km (10 mins)' }
      ],
      brochure_url: '#'
    }
  ];
}
